import React, { useEffect, useState } from "react";
import { Select, Input, DatePicker, Button, message } from "antd";
import moment from "moment";
import { customDateTimeFormat } from "../../../utils/filterHelper";
import { DATE_WITH_HOUR_FORMAT, DATE_WITH_HOUR_MINUTES_FORMAT } from "../../../constants/dateConstants";

/**
 * One column's data filter, shown in a popover from the funnel in its header.
 *
 * This is the board view's copy of what witmeg-reporting-embed-v2 puts in its table
 * headers (components/TableWidget/DataFilters.js), so the preview here matches what a
 * reader gets in the host app. The old Data Filters TAB listed every column's filter
 * in one accordion above the board, which is not what the host app does and made it
 * impossible to tell which column a filter belonged to at a glance.
 *
 * Nothing here queries anything: data filters trim rows already loaded. Apply hands
 * the values up and the existing dataFilterEngine does the work, exactly as the tab
 * did - only the placement changed.
 */

const { Option } = Select;

/* Which comparisons a column offers. "Between" needs an ordering, "Contains" needs
   text, so the two lists are deliberately different. */
const RANGE_OPTIONS = [
  { value: "equal", label: "Equals" },
  { value: "gt", label: "Greater than" },
  { value: "gte", label: "Greater or equal" },
  { value: "lt", label: "Less than" },
  { value: "lte", label: "Less or equal" },
  { value: "between", label: "Between" },
];

const TEXT_OPTIONS = [
  { value: "equal", label: "Equals" },
  { value: "con", label: "Contains" },
];

const optionsFor = (dataType) => (dataType === "number" || dataType === "date" ? RANGE_OPTIONS : TEXT_OPTIONS);

const filled = (v) => v !== null && v !== undefined && v !== "";

/** Everything wrong with what is typed, as a message or null. */
function validate({ filterType, dataType, value, value1, value2 }) {
  if (filterType === "between") {
    // Half a range is not a filter - say so rather than silently ignoring it.
    if (filled(value1) !== filled(value2)) return "Enter both ends of the range.";
    if (!filled(value1)) return "Enter a value first.";
    if (dataType === "number" && Number(value1) > Number(value2)) return "From must not be greater than To.";
    if (dataType === "date" && moment(value1).isAfter(moment(value2))) return "From date must not be after To date.";
    return null;
  }

  if (!filled(value)) return "Enter a value first.";
  if (dataType === "number" && Number.isNaN(Number(value))) return "Enter a number.";
  return null;
}

export default function ColumnDataFilter({ filter, columnTitle, availableFields, onApply, onReset, onClose }) {
  const isBetween = (t) => t === "between";

  const [filterType, setFilterType] = useState(filter?.filterType || optionsFor(filter?.dataType)[0].value);
  /* Seeded from the saved filter: the editor lets an author configure a default, and
     starting empty threw it away and made the reader retype it. */
  const [value, setValue] = useState(filter?.value ?? null);
  const [value1, setValue1] = useState(filter?.value1 ?? null);
  const [value2, setValue2] = useState(filter?.value2 ?? null);

  useEffect(() => {
    setFilterType(filter?.filterType || optionsFor(filter?.dataType)[0].value);
    setValue(filter?.value ?? null);
    setValue1(filter?.value1 ?? null);
    setValue2(filter?.value2 ?? null);
  }, [filter]);

  const options = optionsFor(filter?.dataType);

  const apply = () => {
    const error = validate({ filterType, dataType: filter?.dataType, value, value1, value2 });
    if (error) {
      message.error(error);
      return;
    }

    onApply(filter, {
      filterType,
      value: isBetween(filterType) ? null : value,
      value1: isBetween(filterType) ? value1 : null,
      value2: isBetween(filterType) ? value2 : null,
    });
    if (onClose) onClose();
  };

  const reset = () => {
    setValue(null);
    setValue1(null);
    setValue2(null);
    onReset(filter);
    if (onClose) onClose();
  };

  const renderValue = (current, set, placeholder) => {
    if (filter?.dataType === "date") {
      /* Follows the column's declared format, so a column holding a timestamp gets a
         time picker and the value saved matches what the table shows. */
      const format = customDateTimeFormat(availableFields).getDateFormat(filter.fieldName).dateAndTimeFormat;

      return (
        <DatePicker
          style={{ width: "100%" }}
          value={current ? moment(current) : null}
          format={format}
          showTime={format === DATE_WITH_HOUR_FORMAT || format === DATE_WITH_HOUR_MINUTES_FORMAT}
          placeholder={placeholder}
          onChange={(date, dateString) => set(dateString || null)}
        />
      );
    }

    if (filter?.dataType === "number") {
      return (
        <Input
          type="number"
          value={current ?? ""}
          placeholder={placeholder}
          onChange={(e) => set(e.target.value === "" ? null : e.target.value)}
        />
      );
    }

    return (
      <Input
        value={current ?? ""}
        placeholder={placeholder}
        onChange={(e) => set(e.target.value === "" ? null : e.target.value)}
      />
    );
  };

  return (
    <div className="cdf-popover">
      <div className="cdf-field">
        <label>{columnTitle}</label>
        <Select value={filterType} onChange={setFilterType} style={{ width: "100%" }}>
          {options.map((o) => (
            <Option key={o.value} value={o.value}>
              {o.label}
            </Option>
          ))}
        </Select>
      </div>

      {isBetween(filterType) ? (
        <div className="cdf-range">
          {renderValue(value1, setValue1, "From")}
          <span className="cdf-range-sep">to</span>
          {renderValue(value2, setValue2, "To")}
        </div>
      ) : (
        <div className="cdf-field">{renderValue(value, setValue, "Value")}</div>
      )}

      <div className="cdf-actions">
        <Button size="small" onClick={reset}>
          Reset
        </Button>
        <Button size="small" type="primary" onClick={apply}>
          Apply
        </Button>
      </div>
    </div>
  );
}
